<template>
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeModal">
        <div class="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col" @click.stop>
            <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Anexar Documentos</h3>
                <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <XIcon class="w-5 h-5" />
                </button>
            </div>
            
            <div class="p-6 overflow-y-auto flex-1">
                <!-- Alerta para agendamento não confirmado -->
                <div v-if="!isAppointmentConfirmed" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                        </svg>
                        <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Agendamento não confirmado
                        </h4>
                    </div>
                    <p class="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                        Para anexar documentos, o agendamento deve estar confirmado primeiro. Confirme o agendamento antes de prosseguir.
                    </p>
                </div>

                <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                    <h4 class="font-medium text-gray-900 dark:text-white mb-2">Upload de Documentos</h4>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        Faça upload de múltiplos documentos relacionados ao agendamento. Formatos aceitos: JPG, PNG, PDF.
                    </p>
                </div>

                <!-- Área de upload -->
                <div
                    class="w-full flex flex-col items-center justify-center border-2 border-dashed border-blue-400 dark:border-blue-600 rounded-lg p-8 bg-blue-50 dark:bg-blue-900/10 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors relative"
                    @click.stop="triggerFileInput"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDropFile"
                    :class="{ 'ring-2 ring-blue-400 dark:ring-blue-500': isDragActive }"
                >
                    <UploadIcon class="w-10 h-10 text-blue-500 mb-2" />
                    <span class="text-base text-blue-700 dark:text-blue-200 font-medium">
                        Arraste arquivos aqui ou clique para selecionar
                    </span>
                    <span class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        JPG, PNG, PDF (máx. 10MB cada)
                    </span>
                    <input
                        type="file"
                        ref="fileInput"
                        @change="handleFileSelect"
                        accept=".jpg,.jpeg,.png,.pdf"
                        multiple
                        class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        style="z-index:2;"
                        tabindex="-1"
                        @click.stop
                    />
                </div>

                <!-- Lista de documentos selecionados -->
                <div v-if="selectedFiles.length > 0" class="mt-6">
                    <h4 class="font-medium text-gray-900 dark:text-white mb-4">Documentos Selecionados ({{ selectedFiles.length }})</h4>
                    
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div v-for="(file, index) in selectedFiles" :key="index" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <!-- Cabeçalho do documento -->
                            <div class="flex items-center justify-between mb-3">
                                <div class="flex items-center gap-2">
                                    <FileIcon class="w-5 h-5 text-blue-500" />
                                    <span class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ file.file.name }}</span>
                                </div>
                                <div class="flex items-center gap-1">
                                    <!-- Botão de preview -->
                                    <button 
                                        @click="previewFile(file)"
                                        class="p-1 text-blue-500 hover:text-blue-700 rounded"
                                        title="Visualizar documento"
                                    >
                                        <EyeIcon class="w-4 h-4" />
                                    </button>
                                    <!-- Botão de remoção -->
                                    <button 
                                        @click="removeFile(index)"
                                        class="p-1 text-red-500 hover:text-red-700 rounded"
                                        title="Remover documento"
                                    >
                                        <XIcon class="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            
                            <!-- Informações do arquivo -->
                            <div class="text-xs text-gray-500 dark:text-gray-500 mb-3">
                                {{ formatFileSize(file.file.size) }}
                            </div>
                            
                            <!-- Configurações do documento -->
                            <div class="space-y-3">
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tipo de Documento*
                                    </label>
                                    <select 
                                        v-model="file.documentType" 
                                        required
                                        class="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
                                    >
                                        <option value="">Selecione o tipo</option>
                                        <option value="guia">Guia de Autorização</option>
                                        <option value="laudo">Laudo Médico</option>
                                        <option value="prescricao">Prescrição Médica</option>
                                        <option value="exame_anterior">Exame Anterior</option>
                                        <option value="documento_identidade">Documento de Identidade</option>
                                        <option value="outro">Outro</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Descrição
                                    </label>
                                    <input 
                                        v-model="file.description" 
                                        type="text"
                                        placeholder="Breve descrição"
                                        class="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Mensagem quando não há arquivos -->
                <div v-if="selectedFiles.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                    <UploadIcon class="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum documento selecionado.</p>
                    <p class="text-sm mt-2">Selecione um ou mais arquivos para anexar.</p>
                </div>
            </div>
            
            <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button 
                    @click="closeModal" 
                    class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                    Cancelar
                </button>
                <button 
                    @click="saveDocuments" 
                    :disabled="!hasValidDocuments || isProcessing || !isAppointmentConfirmed"
                    class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    <template v-if="isProcessing">
                        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Processando...</span>
                    </template>
                    <template v-else-if="!isAppointmentConfirmed">
                        <span>Agendamento não confirmado</span>
                    </template>
                    <template v-else>
                        <SaveIcon class="w-4 h-4" />
                        <span>Anexar {{ selectedFiles.length }} Documento{{ selectedFiles.length !== 1 ? 's' : '' }}</span>
                    </template>
                </button>
            </div>
        </div>

        <!-- Modal de Preview -->
        <div v-if="previewModal.show" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4" @click="closePreview">
            <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col" @click.stop>
                <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                        Visualizar: {{ previewModal.file?.file.name }}
                    </h3>
                    <button @click="closePreview" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <XIcon class="w-5 h-5" />
                    </button>
                </div>
                
                <div class="p-4 overflow-y-auto flex-1">
                    <!-- Preview de imagem -->
                    <div v-if="previewModal.isImage" class="flex justify-center">
                        <img 
                            :src="previewModal.url" 
                            :alt="previewModal.file?.file.name"
                            class="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                        />
                    </div>
                    
                    <!-- Preview de PDF -->
                    <div v-else-if="previewModal.isPDF" class="flex justify-center">
                        <iframe 
                            :src="previewModal.url"
                            class="w-full h-[60vh] border border-gray-300 dark:border-gray-600 rounded-lg"
                            title="PDF Preview"
                        ></iframe>
                    </div>
                    
                    <!-- Fallback para outros tipos -->
                    <div v-else class="text-center py-12">
                        <FileIcon class="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <p class="text-gray-600 dark:text-gray-400">
                            Visualização não disponível para este tipo de arquivo.
                        </p>
                        <p class="text-sm text-gray-500 dark:text-gray-500 mt-2">
                            {{ previewModal.file?.file.name }}
                        </p>
                    </div>
                </div>
                
                <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <button @click="closePreview" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { 
    XIcon, 
    UploadIcon, 
    FileIcon, 
    SaveIcon,
    EyeIcon
} from 'lucide-vue-next'
import apiClient from '@/services/authService'

// Props
// eslint-disable-next-line no-undef
const props = defineProps({
    appointmentId: {
        type: [Number, String],
        required: true
    },
    scheduleId: {
        type: [Number, String],
        required: true
    },
    isAppointmentConfirmed: {
        type: Boolean,
        required: true
    }
})

// Emits
// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'document-saved'])

// Estado reativo
const isProcessing = ref(false)
const isDragActive = ref(false)
const selectedFiles = ref([])
const previewModal = ref({
    show: false,
    file: null,
    url: null,
    isImage: false,
    isPDF: false
})

// Refs para elementos DOM
const fileInput = ref(null)

// Computed properties
const hasValidDocuments = computed(() => {
    return selectedFiles.value.length > 0 && selectedFiles.value.every(file => file.documentType)
})

// Métodos
const closeModal = () => {
    // Reseta a lista de arquivos
    selectedFiles.value = []
    // Fecha o modal
    emit('close')
}

const triggerFileInput = () => {
    if (fileInput.value) {
        fileInput.value.click()
    }
}

const onDragOver = () => {
    isDragActive.value = true
}

const onDragLeave = () => {
    isDragActive.value = false
}

const onDropFile = (event) => {
    isDragActive.value = false
    const files = event.dataTransfer.files
    if (files && files.length > 0) {
        handleFileSelect({ target: { files } })
    }
}

const handleFileSelect = (event) => {
    event.preventDefault()
    event.stopPropagation()
    
    const files = Array.from(event.target.files || [])
    if (!files.length) return
    
    files.forEach(file => {
        // Validação do tipo de arquivo
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
        if (!allowedTypes.includes(file.type)) {
            alert(`Tipo de arquivo não suportado: ${file.name}. Use JPG, PNG ou PDF.`)
            return
        }
        
        // Validação do tamanho (10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert(`Arquivo muito grande: ${file.name}. Tamanho máximo: 10MB.`)
            return
        }
        
        // Verifica se o arquivo já foi adicionado
        const isDuplicate = selectedFiles.value.some(existingFile => 
            existingFile.file.name === file.name && 
            existingFile.file.size === file.size
        )
        
        if (isDuplicate) {
            console.log(`Arquivo já adicionado: ${file.name}`)
            return
        }
        
        // Adiciona o arquivo à lista com configurações padrão
        selectedFiles.value.push({
            file: file,
            documentType: '',
            description: ''
        })
    })
}

const removeFile = (index) => {
    selectedFiles.value.splice(index, 1)
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const previewFile = (fileData) => {
    const file = fileData.file
    const isImage = file.type.startsWith('image/')
    const isPDF = file.type === 'application/pdf'
    
    // Cria URL para preview
    const url = URL.createObjectURL(file)
    
    previewModal.value = {
        show: true,
        file: fileData,
        url: url,
        isImage: isImage,
        isPDF: isPDF
    }
}

const closePreview = () => {
    if (previewModal.value.url) {
        URL.revokeObjectURL(previewModal.value.url)
    }
    previewModal.value = {
        show: false,
        file: null,
        url: null,
        isImage: false,
        isPDF: false
    }
}

const saveDocuments = async () => {
    if (!hasValidDocuments.value) {
        alert('Por favor, selecione pelo menos um documento e escolha o tipo para cada um.')
        return
    }
    
    isProcessing.value = true
    
    try {
        // Salva cada documento individualmente
        const savedDocuments = []
        
        for (const fileData of selectedFiles.value) {
            const formData = new FormData()
            formData.append('file', fileData.file)
            formData.append('document_type', fileData.documentType)
            formData.append('description', fileData.description || '')
            formData.append('appointment_id', props.appointmentId)
            formData.append('schedule_id', props.scheduleId)
            
            // Envia para o novo endpoint de documentos
            const response = await apiClient.post('/api/v1/documents/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            
            if (response.data.success) {
                savedDocuments.push({
                    ...response.data,
                    originalFile: fileData.file.name,
                    documentType: fileData.documentType,
                    description: fileData.description
                })
            } else {
                // Verifica se é erro de agendamento não confirmado
                if (response.data.message && response.data.message.includes('confirmado')) {
                    alert('⚠️ ' + response.data.message + '\n\nPara anexar documentos, o agendamento deve estar confirmado primeiro.')
                } else {
                    throw new Error(response.data.message)
                }
                return // Para o processo se for erro de confirmação
            }
        }
        
        emit('document-saved', savedDocuments)
        closeModal()
        
    } catch (error) {
        console.error('Erro ao salvar documentos:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido'
        
        // Verifica se é erro de agendamento não confirmado
        if (errorMessage.includes('confirmado')) {
            alert('⚠️ ' + errorMessage + '\n\nPara anexar documentos, o agendamento deve estar confirmado primeiro.')
        } else {
            alert('Erro ao salvar documentos: ' + errorMessage)
        }
    } finally {
        isProcessing.value = false
    }
}
</script>

<style scoped>
/* Estilos específicos do componente */
</style> 